import * as React from 'react';

import { cn } from '@/ux-common/lib/utils';

export type InlineProps<T extends keyof React.JSX.IntrinsicElements = 'div'> = {
  /**
   * The element to render as.
   * @default 'div'
   */
  as?: T;
  /**
   * Content to be arranged horizontally.
   */
  children: React.ReactNode;
  /**
   * Additional className for the container. Merged with the `.inline-row` base
   * class via cn(). Apply a gap modifier here — e.g. `className="inline-row-sm"`.
   */
  className?: string;
} & Omit<React.ComponentPropsWithoutRef<T>, 'children' | 'className'>;

/**
 * Inline — horizontal layout primitive for arranging items in a row.
 *
 * All visual layout is applied via CSS classes. This wrapper provides:
 * - The `.inline-row` base class applied automatically (horizontal flex row,
 *   vertically centered, wrapping enabled, 20px gap)
 * - Polymorphism via `as` (default `'div'`), rendered with `React.createElement`
 * - `data-component="Inline"` for component identification (consumer can override)
 * - `className` merging via `cn()`
 * - Full HTML attribute passthrough (id, aria-label, role, etc.)
 *
 * Gap is controlled entirely through the CSS class API — the base `.inline-row`
 * carries the default 20px gap, and a flat
 * `.inline-row-{none|thin|xs|sm|default}` modifier (applied via `className`)
 * overrides it. There is no `gap` prop.
 *
 * The base class is `.inline-row` (not `.inline`) because `inline` is a Tailwind
 * display utility — a `.inline` class would be overridden by `display: inline`.
 *
 * Wrapping is on by default (the `.inline-row` base applies `flex-wrap`). To opt
 * out, add the `flex-nowrap` utility via `className`. There is no `wrap` prop.
 *
 * Inline is inherently horizontal — use Stack for vertical layouts. There is no
 * ref forwarding (the polymorphic `React.createElement` form does not forward a ref).
 *
 * @example
 * ```tsx
 * // Default 20px gap, wrapping enabled
 * <Inline>
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 * </Inline>
 *
 * // Tighter gap via the modifier class
 * <Inline className="inline-row-sm">
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 * </Inline>
 *
 * // Disable wrapping with the Tailwind utility
 * <Inline className="inline-row-sm flex-nowrap">
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 * </Inline>
 *
 * // Render as a different element with native attributes
 * <Inline as="nav" className="inline-row-thin" aria-label="Primary navigation">
 *   <a href="/home">Home</a>
 *   <a href="/settings">Settings</a>
 * </Inline>
 *
 * // className merges with the `.inline-row` base class (does not replace)
 * <Inline className="inline-row-sm p-4 border">
 *   <div>Padded, bordered inline row</div>
 * </Inline>
 *
 * // Override data-component for feature-scoped semantics
 * <Inline data-component="ToolbarInline" data-testid="toolbar-inline">
 *   <div>...</div>
 * </Inline>
 * ```
 */
export const Inline = <T extends keyof React.JSX.IntrinsicElements = 'div'>({
  as,
  className,
  children,
  ...props
}: InlineProps<T>) => {
  const Component = as || 'div';

  return React.createElement(
    Component,
    {
      'data-component': 'Inline',
      className: cn('inline-row', className),
      ...props,
    },
    children
  );
};

Inline.displayName = 'Inline';
